import { Alert, Box, Chip, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { formatDateTime } from "@manna/ui/date";
import { supabase } from "../../lib/supabase";
import { TerritoryPicker } from "../../components/TerritoryPicker";
import { RepPicker } from "../../components/RepPicker";
import { UserPicker } from "../../components/UserPicker";
import { useHasAnyRole } from "../../hooks/useHasRole";
import { useDeal, useUpdateDealAssignment } from "../../queries/deals";

// "Deal Team" rail section: Territory → AE → BDR (routing), Owner, and the
// read-only Record audit. Territory is first because it narrows the AE/BDR
// pickers to that territory's reps (RepPicker territoryId → cascade). Each field
// saves on change via a partial manna_update_deal (the RPC enforces the
// manager / revenue_leader gates + the row_version optimistic lock).

/** First+last initial of a display name, e.g. "Jane Doe" → "JD". */
export function initials(name: string | null | undefined): string {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : "";
  return (first + last).toUpperCase();
}

interface DealTeamSummaryRow {
  id: string;
  territory_name: string | null;
  ae_user_name: string | null;
  bdr_user_name: string | null;
}

/** Names for the collapsed summary, read from manna_deals_view (which resolves
 *  territory/AE/BDR names the base deals table doesn't carry). */
export function useDealTeamSummary(dealId: string, enabled = true) {
  return useQuery({
    queryKey: ["deal_team_summary", dealId],
    enabled: enabled && Boolean(dealId),
    staleTime: 30_000,
    queryFn: async (): Promise<DealTeamSummaryRow | null> => {
      const { data, error } = await supabase
        .from("manna_deals_view")
        .select("id, territory_name, ae_user_name, bdr_user_name")
        .eq("id", dealId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as DealTeamSummaryRow | null;
    },
  });
}

/** Collapsed summary: Territory + AE/BDR initial chips (rendered in the rail
 *  section header). */
export function DealTeamSummary({ dealId }: { dealId: string }) {
  const { data } = useDealTeamSummary(dealId);
  if (!data) return null;
  const chip = (role: "AE" | "BDR", name: string | null) =>
    name ? (
      <Tooltip title={`${role}: ${name}`}>
        <Chip size="small" label={initials(name)} sx={{ height: 18, fontSize: 10 }} />
      </Tooltip>
    ) : null;
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {data.territory_name ?? "No territory"}
      </Typography>
      {chip("AE", data.ae_user_name)}
      {chip("BDR", data.bdr_user_name)}
    </Stack>
  );
}

function GroupLabel({ children }: { children: string }) {
  return (
    <Typography
      variant="overline"
      sx={{ fontSize: 9.5, letterSpacing: 1, color: "text.disabled", lineHeight: 2 }}
    >
      {children}
    </Typography>
  );
}

export function DealTeamSection({ dealId }: { dealId: string }) {
  const { data: deal, isLoading } = useDeal(dealId);
  const update = useUpdateDealAssignment();

  // Same gates as the deal form: managers reassign AE/BDR/Owner; only a
  // revenue_leader (or super admin) changes territory. The RPC re-checks.
  const isManager = useHasAnyRole("manna", ["revenue_leader", "territory_manager"]);
  const canEditTerritory = useHasAnyRole("manna", ["revenue_leader", "platform_super_admin"]);

  if (isLoading) return <CircularProgress size={18} />;
  if (!deal) return null;

  const save = (patch: {
    territory_id?: string | null;
    ae_user_id?: string | null;
    bdr_user_id?: string | null;
    owner_user_id?: string | null;
  }) => update.mutate({ id: deal.id, row_version: deal.row_version, ...patch });

  const busy = update.isPending;

  return (
    <Stack spacing={1.5}>
      <Box>
        <GroupLabel>Routing</GroupLabel>
        <Stack spacing={1.5} mt={0.5}>
          <TerritoryPicker
            label={canEditTerritory ? "Territory" : "Territory (read-only)"}
            value={deal.territory_id ?? null}
            onChange={(id) => save({ territory_id: id })}
            disabled={!canEditTerritory || busy}
          />
          <RepPicker
            role="ae"
            label="Account Executive"
            value={deal.ae_user_id ?? null}
            onChange={(id) => save({ ae_user_id: id })}
            territoryId={deal.territory_id ?? null}
            disabled={!isManager || busy}
          />
          <RepPicker
            role="bdr"
            label="BDR"
            value={deal.bdr_user_id ?? null}
            onChange={(id) => save({ bdr_user_id: id })}
            territoryId={deal.territory_id ?? null}
            disabled={!isManager || busy}
          />
        </Stack>
      </Box>

      <Box>
        <GroupLabel>Owner</GroupLabel>
        <Box mt={0.5}>
          <UserPicker
            label="Owner"
            value={deal.owner_user_id ?? null}
            onChange={(id) => save({ owner_user_id: id })}
            disabled={!isManager || busy}
          />
        </Box>
      </Box>

      <Box>
        <GroupLabel>Record</GroupLabel>
        <Stack spacing={0.25} mt={0.25}>
          <Typography variant="caption" color="text.secondary">
            Created {formatDateTime(deal.created_at)}
            {deal.created_by_name ? ` by ${deal.created_by_name}` : ""}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Updated {formatDateTime(deal.updated_at)}
            {deal.updated_by_name ? ` by ${deal.updated_by_name}` : ""}
          </Typography>
        </Stack>
      </Box>

      {update.isError && (
        <Alert severity="error" sx={{ py: 0 }}>
          {update.error instanceof Error ? update.error.message : "Failed to save."}
        </Alert>
      )}
    </Stack>
  );
}
